import { ScenariosRequest } from '@api';
import { deepEqual, getTimeTillEndOfDayMs } from '@helpers';
import { QueryClient } from '@tanstack/react-query';
import { ActiveFilterOptions } from '@terminal/components/filters/Filters';
import {
  FilterOption,
  getDefaultChildFilters,
  updateFilterOptions,
} from '@terminal/components/update-filter-options/updateFilterOptions';
import { INDEX_ID, WATCHLIST_ID } from '@terminal/config/constants';
import {
  createScenarioRequest,
  createScenariosQueryOptions,
} from '@terminal/react-query-hooks/scenarios/useScenarios.utils';
import {
  createPublicListEntitiesQueryOptions,
  createPublicListsQueryOptions,
} from '@terminal/react-query-hooks/usePublicLists';
import { queryClient } from '@terminal/services/query-client/queryClient';
import { withGcTime } from '@terminal/services/query-client/utils';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import { getFirstPopulatedWatchlist } from '@terminal/stores/use-watchlist/useWatchlist.utils';
import { SCENARIO_ASSET_FILTER_KEY } from '@terminal/widgets/scenario/hooks/use-scenario-store/filter-utils';
import {
  createScenarioFallbackSnapshot,
  type ScenarioFallbackInput,
  type ScenarioFallbackSnapshot,
} from '@terminal/widgets/scenario/hooks/use-scenario-store/scenario-fallback';
import { useScenarioStore } from '@terminal/widgets/scenario/hooks/use-scenario-store/useScenarioStore';
import { mapFilterToCondition } from '@terminal/widgets/scenario/utils/scenario-conditions/scenario-conditions';
import { getConditionFilters } from '@terminal/widgets/scenario/utils/scenario-conditions-filters/scenario-conditions-filters';
import { t } from 'i18next';

const NASDAQ_100_LIST_NAME = 'Nasdaq 100';
const RISK_OFF_FILTER_KEY = 'risk-off';
const SP_500_DROPS_OPTION_KEY = 'spx_500_drops_2_d';
const INTEREST_RATE_FILTER_KEY = 'interest-rate';
const YIELD_RISES_OPTION_KEY = 'rises_10bps_w';

interface ResolveInitialScenarioFallbackProps {
  client?: QueryClient;
  gcTime?: number;
}

const getConditionFilter = (filterKey: string) => {
  const filter = getConditionFilters(t).find(item => item.key === filterKey);

  if (!filter) {
    throw new Error(`Scenario condition filter not found: ${filterKey}`);
  }

  return filter;
};

const createConditionFilterOption = ({
  filterKey,
  optionKey,
}: {
  filterKey: string;
  optionKey: string;
}) => {
  const filter = getConditionFilter(filterKey);
  const option = filter.config.options.find(item => item.key === optionKey);

  if (!option) {
    throw new Error(`Scenario condition option not found: ${optionKey}`);
  }

  return updateFilterOptions({
    existingFilters: [],
    filterKey,
    option: option as FilterOption,
    defaultChildFilterOptions: getDefaultChildFilters(filterKey, [filter]),
  })[0];
};

const createAssetFilterOption = ({
  id,
  name,
  valueKey,
}: {
  id: string;
  name: string;
  valueKey: typeof INDEX_ID | typeof WATCHLIST_ID;
}) =>
  ({
    id: SCENARIO_ASSET_FILTER_KEY,
    filter: SCENARIO_ASSET_FILTER_KEY,
    options: [
      {
        key: id,
        name,
        value: { [valueKey]: id },
      },
    ],
  }) satisfies ActiveFilterOptions;

const createScenarioFallback = ({
  assetFilterOption,
  conditionFilterOption,
  entityTags,
}: Omit<ScenarioFallbackInput, 'requestBody'>): ScenarioFallbackInput => {
  const condition = mapFilterToCondition(conditionFilterOption);

  return {
    assetFilterOption,
    conditionFilterOption,
    entityTags,
    requestBody: createScenarioRequest({
      conditions: [condition],
      entityTags,
    }),
  };
};

const resolveWatchlistFallback = (): ScenarioFallbackInput | undefined => {
  const { error, lists } = useWatchlist.getState();

  if (error) {
    return undefined;
  }

  const watchlist = getFirstPopulatedWatchlist(lists);

  if (!watchlist) {
    return undefined;
  }

  const entityTags = watchlist.entities.flatMap(entity =>
    entity.tag ? [entity.tag] : []
  );

  return createScenarioFallback({
    assetFilterOption: createAssetFilterOption({
      id: watchlist.id,
      name: watchlist.name,
      valueKey: WATCHLIST_ID,
    }),
    conditionFilterOption: createConditionFilterOption({
      filterKey: RISK_OFF_FILTER_KEY,
      optionKey: SP_500_DROPS_OPTION_KEY,
    }),
    entityTags,
  });
};

const resolveNasdaqFallback = async ({
  client,
  gcTime,
}: {
  client: QueryClient;
  gcTime?: number;
}) => {
  const publicLists = await client.ensureQueryData(
    withGcTime(createPublicListsQueryOptions(), gcTime)
  );
  const nasdaq100 = publicLists.find(
    list => list.name === NASDAQ_100_LIST_NAME
  );

  if (!nasdaq100) {
    throw new Error('Nasdaq 100 public list not found');
  }

  const list = await client.ensureQueryData(
    withGcTime(createPublicListEntitiesQueryOptions(nasdaq100.id), gcTime)
  );
  const entityTags = list.entities.map(entity => entity.tag);

  if (!entityTags.length) {
    throw new Error('Nasdaq 100 public list is empty');
  }

  return createScenarioFallback({
    assetFilterOption: createAssetFilterOption({
      id: nasdaq100.id,
      name: nasdaq100.name,
      valueKey: INDEX_ID,
    }),
    conditionFilterOption: createConditionFilterOption({
      filterKey: INTEREST_RATE_FILTER_KEY,
      optionKey: YIELD_RISES_OPTION_KEY,
    }),
    entityTags,
  });
};

export const resolveInitialScenarioFallback = async ({
  client = queryClient,
  gcTime,
}: ResolveInitialScenarioFallbackProps = {}) =>
  resolveWatchlistFallback() ?? resolveNasdaqFallback({ client, gcTime });

export const prefetchFallbackScenarioData = async () => {
  try {
    const cacheTime = getTimeTillEndOfDayMs();
    const fallback = await resolveInitialScenarioFallback({
      gcTime: cacheTime,
    });

    await queryClient.prefetchQuery(
      createScenariosQueryOptions({
        body: fallback.requestBody,
        queryClient,
        gcTime: cacheTime,
      })
    );
  } catch {
    return;
  }
};

interface InitializeScenarioFallbackProps {
  hasAssistantConditions: () => boolean;
  isInitializationActive: () => boolean;
  sendMessage: (body: ScenariosRequest) => Promise<void>;
}

const hasExistingScenarioInput = (hasAssistantConditions: () => boolean) => {
  const state = useScenarioStore.getState();
  const hasFilterOptions = Object.values(state.filterGroups).some(
    group => group.activeFilterOptions.length > 0
  );

  return (
    hasFilterOptions ||
    hasAssistantConditions() ||
    state.scenarioPageEntityTags.length > 0 ||
    !!state.lastConditions?.length
  );
};

export const hasAppliedScenarioChanged = ({
  currentSnapshot,
  fallbackSnapshot,
  hasAssistantConditions,
}: {
  currentSnapshot: ScenarioFallbackSnapshot;
  fallbackSnapshot?: ScenarioFallbackSnapshot;
  hasAssistantConditions: boolean;
}) => {
  return (
    !fallbackSnapshot ||
    !deepEqual(currentSnapshot, fallbackSnapshot) ||
    hasAssistantConditions
  );
};

export const initializeScenarioFallback = async ({
  hasAssistantConditions,
  isInitializationActive,
  sendMessage,
}: InitializeScenarioFallbackProps) => {
  const initialState = useScenarioStore.getState();

  if (!isInitializationActive()) {
    return;
  }

  if (initialState.scenarioFallback.status === 'dismissed') {
    return;
  }

  if (initialState.scenarioFallback.status === 'applied') {
    const currentSnapshot = createScenarioFallbackSnapshot({
      filterGroups: initialState.filterGroups,
      entityTags: initialState.scenarioPageEntityTags,
    });

    if (
      hasAppliedScenarioChanged({
        currentSnapshot,
        fallbackSnapshot: initialState.scenarioFallback.snapshot,
        hasAssistantConditions: hasAssistantConditions(),
      })
    ) {
      initialState.dismissScenarioFallback();
      return;
    }

    if (isInitializationActive()) {
      await sendMessage(initialState.scenarioFallback.requestBody);
    }
    return;
  }

  if (hasExistingScenarioInput(hasAssistantConditions)) {
    initialState.dismissScenarioFallback();
    return;
  }

  let fallback: ScenarioFallbackInput;

  try {
    fallback = await resolveInitialScenarioFallback();
  } catch {
    return;
  }

  if (!isInitializationActive()) {
    return;
  }

  const latestState = useScenarioStore.getState();

  if (latestState.scenarioFallback.status !== 'pending') {
    return;
  }

  if (hasExistingScenarioInput(hasAssistantConditions)) {
    latestState.dismissScenarioFallback();
    return;
  }

  latestState.applyScenarioFallback(fallback);

  if (!isInitializationActive()) {
    return;
  }

  await sendMessage(fallback.requestBody);
};
